import { useEffect, useState, type ChangeEvent } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SendIcon from '@mui/icons-material/Send';
import ReplayIcon from '@mui/icons-material/Replay';
import UndoIcon from '@mui/icons-material/Undo';
import { useNotify, useRecordContext, useRefresh, type RaRecord } from 'react-admin';
import { SectionCard } from '../shared/Panel';
import {
  fetchViaWritePreview,
  approveViaWrite,
  retryViaWrite,
  withdrawFromVia,
  generateViaFile,
  exportToTms,
  confirmNotDuplicate,
  toErrorMessage,
  viaDestinationLabel,
  FEATURE_VIA_WRITE_DUPLICATE_CHECK,
  type ViaWritePreview,
  type ViaLastWrite,
} from './viaWriteApi';
import { ViaWriteDuplicates } from './ViaWriteDuplicates';
import { TmsExportDialog } from './TmsExportDialog';
import { AttemptTrail, DeliveryRetryInfo } from './DeliveryRetryInfo';
import { useFeature } from '../admin/featureFlags/FeatureGate';
import { recordScoped } from '../shared/recordScoped';
import { plural } from '../shared/plural';
import { FEATURE_TMS_EXPORT_ONE_STEP, VIA_WRITE_IN_VIA_STATES, viaFileStateName } from '../shared/status';
import { shortDateTime } from '../shared/dateTime';

/**
 * Dokąd pójdzie plik i jak skończył się ostatni zapis (15.10.2026).
 *
 * Tryb dostarczania ustawia wdrożenie (env), nie operator — tu tylko
 * odczyt. „SFTP prod” wyróżniony, bo każdy plik tam to prawdziwe zlecenie
 * w TMS; adresu serwera ekran nie dostaje.
 */
const DeliveryStatus = ({ preview, tmsNames }: { preview: ViaWritePreview; tmsNames: boolean }) => {
  const delivery = preview.delivery;
  const last = preview.lastWrite;
  if (!delivery && !last) return null;
  return (
    <Box sx={{ mb: 1 }}>
      {delivery && (
        <Chip
          size="small"
          data-testid="via-delivery-mode"
          data-mode={delivery.target ?? delivery.mode ?? 'unknown'}
          variant="outlined"
          color={
            // Katalog na serwerze aplikacji: plik nie trafia do TMS (25.10.2026).
            delivery.reachesTms === false
              ? 'error'
              : delivery.target === 'prod'
                ? 'warning'
                : delivery.mode === 'sftp'
                  ? 'info'
                  : 'default'
          }
          label={`Dostarczanie: ${delivery.label}`}
        />
      )}
      {last && (
        <Typography
          variant="caption"
          color="text.secondary"
          data-testid="via-last-write"
          sx={{ display: 'block', mt: 0.5 }}
        >
          Ostatni zapis: {viaFileStateName(last.status, tmsNames)} · próba {last.attempt}
          {last.destination ? ` · ${viaDestinationLabel(last.destination)}` : ''}
          {last.deliveredAt ? ` · dostarczono ${shortDateTime(last.deliveredAt)}` : ''}
        </Typography>
      )}
      {last?.status === 'FAILED' && last.errorMessage && (
        <Alert severity="error" sx={{ mt: 0.5 }} data-testid="via-last-write-error">
          {last.errorMessage}
        </Alert>
      )}
    </Box>
  );
};

/**
 * Dwa pliki jednej akcji (16.10.2026, odpowiedzi Marathonu/Euvic, pyt. 39e):
 * zlecenie z wózkiem to w TMS dwa rekordy — transport i czynność wózkowa
 * (typ FORKLIFT, numer klienta z dopiskiem „-liczbaXPALF”). Operator widzi
 * oba pliki, ich braki i stan ostatniego zapisu każdego z nich.
 */
const WriteFiles = ({ preview, tmsNames }: { preview: ViaWritePreview; tmsNames: boolean }) => {
  const files = preview.files ?? [];
  if (files.length < 2) return null;
  const lastOf = (kind: string): ViaLastWrite | undefined =>
    (preview.lastWrites ?? []).find((w) => (w.recordKind ?? 'MAIN') === kind);
  return (
    <Alert severity="info" sx={{ mb: 1 }} data-testid="via-write-files">
      <AlertTitle>Do VIA pójdą {files.length} pliki</AlertTitle>
      <Typography variant="body2" sx={{ mb: 0.5 }}>
        Zlecenie wymaga wózka, więc obok transportu powstaje osobna czynność wózkowa. Jej cenę i
        liczbę czynności wpisz w „Dane dla importera VIA”. Oba pliki wychodzą jednym kliknięciem.
      </Typography>
      <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
        {files.map((f) => {
          const last = lastOf(f.kind);
          return (
            <li key={f.kind} data-testid={`via-write-file-${f.kind}`}>
              <Typography variant="body2">
                <strong>{f.label}</strong> · {f.fileName} ·{' '}
                {f.errors.length === 0
                  ? 'komplet pól'
                  : `brakuje ${plural(f.errors.length, 'pola', 'pól', 'pól')}`}
                {last ? ` · ostatni zapis: ${viaFileStateName(last.status, tmsNames)}` : ''}
              </Typography>
            </li>
          );
        })}
      </Box>
    </Alert>
  );
};

const DRAFT = 'DRAFT';
/** Stany, z których eksport jedną akcją zatwierdza i wytwarza pliki (21.10.2026). */
const EXPORTABLE = ['DRAFT', 'APPROVED'];
/** Stany, w których zlecenie jest już u klienta — nadpisać się nie da. */
const IN_VIA = VIA_WRITE_IN_VIA_STATES;
const CONFIRMED_IN_TMS = 'CONFIRMED_IN_TMS';

/**
 * „Jest w TMS” (16.10.2026; nazwa — decyzja użytkownika 16.10.2026):
 * zlecenie widać w imporcie z TMS — import znalazł numer zamówienia klienta
 * (od 16.10.2026, pyt. 27) albo nasz numer, albo operator połączył wiersze
 * ręcznie. Kto je tam wprowadził, nie
 * wiemy (my do TMS nic nie zapisujemy); plik byłby duplikatem. Numer TMS
 * i chwila połączenia z koperty (`Order`).
 */
const TmsConfirmation = ({ record, tmsNames }: { record: RaRecord; tmsNames: boolean }) => {
  const number = typeof record.tmsConfirmedNumber === 'string' ? record.tmsConfirmedNumber : null;
  const at = typeof record.tmsConfirmedAt === 'string' ? record.tmsConfirmedAt : null;
  return (
    <Alert severity="success" sx={{ mb: 1 }} data-testid="via-tms-confirmation">
      {/* „Zapisane w TMS” — paczka p. Mariusza 21.10 (BR-ADM-10), przełącznik `tmsExportOneStep`. */}
      {tmsNames ? 'Zapisane w TMS' : 'Jest w TMS'}
      {number ? ` jako ${number}` : ''}
      {at ? ` (od ${shortDateTime(at)})` : ''} — zlecenie widać w imporcie z TMS (rozpoznane po numerze
      zamówienia klienta albo naszym, albo połączone ręcznie). Pliku nie wytworzymy, byłby w TMS
      duplikatem — zmiana wymaga usunięcia zlecenia w VIA i wprowadzenia ponownie.
    </Alert>
  );
};

/**
 * Zapis zlecenia do VIA — panel operatora (W10).
 *
 * ## Dlaczego trzy przyciski, a nie jeden
 *
 * CLAUDE.md: „każdy zapis do VIA wyłącznie po jawnej akcji/akceptacji
 * operatora, nigdy automatem". Podgląd, zatwierdzenie i wytworzenie pliku to
 * trzy osobne czynności — jeden przycisk „wyślij" zamieniłby akceptację
 * w kliknięcie, którego skutków nikt nie widział.
 *
 * ## Dlaczego pokazujemy braki, a nie „dane niepełne"
 *
 * Importer VIA **nie zgłasza odrzucenia**. Plik bez kompletu pól albo nie
 * utworzy zlecenia, albo utworzy je błędnie — i nikt się o tym nie dowie.
 * Lista konkretnych braków jest jedynym momentem, w którym da się to
 * naprawić, więc pokazujemy ją całą, zamiast streszczać.
 *
 * ## Dlaczego podgląd XML jest widoczny
 *
 * Operator bierze na siebie odpowiedzialność za treść, która pójdzie do
 * systemu klienta. Akceptowanie czegoś, czego nie widać, jest akceptacją
 * tylko z nazwy.
 */
const ViaWriteBody = ({ onChanged }: { onChanged?: () => void }) => {
  const record = useRecordContext<RaRecord & { viaWriteStatus?: string }>();
  const notify = useNotify();
  const refresh = useRefresh();

  const [preview, setPreview] = useState<ViaWritePreview | null>(null);
  const [busy, setBusy] = useState(false);
  const [xmlVisible, setXmlVisible] = useState(false);
  const [withdrawReason, setWithdrawReason] = useState('');
  const [notDuplicateReason, setNotDuplicateReason] = useState('');
  // Wynik nieudanej akcji zostaje na ekranie, dopóki operator go nie zamknie
  // albo nie spróbuje znowu (25.10.2026, decyzja użytkownika 24.10.2026: „jak
  // nie działa to nie działa, daj tam errora po kliknięciu”) — sam dymek
  // znikał po kilku sekundach, a okno eksportu zostawało otwarte z odmową.
  const [actionError, setActionError] = useState<{ title: string; message: string } | null>(null);
  const duplicatesOn = useFeature(FEATURE_VIA_WRITE_DUPLICATE_CHECK);
  // Eksport do TMS jedną akcją i nazwy „wysłane / zapisane w TMS” (paczka
  // p. Mariusza 21.10, BR-ADM-18 i BR-ADM-10; S-21.10-11).
  const tmsExport = useFeature(FEATURE_TMS_EXPORT_ONE_STEP);
  const [exportOpen, setExportOpen] = useState(false);
  // Wersja rekordu, dla której policzono podgląd. Zapis „Danych dla
  // importera” w panelu obok cofa zlecenie do szkicu — rekord się odświeża,
  // a podgląd dalej mówił „Plik ma komplet” z aktywnym „Wytwórz plik”, który
  // kończył się 409 (e2e na dev, 28.09.2026).
  const recordVersion = `${String(record?.viaWriteStatus ?? DRAFT)}|${String(
    record?.updatedAt ?? '',
  )}`;
  const [previewVersion, setPreviewVersion] = useState<string | null>(null);
  const stale = preview !== null && previewVersion !== recordVersion;

  // Po zmianie rekordu podgląd liczymy od nowa — tylko jeśli ktoś go już
  // otworzył (samo wejście na zlecenie nadal niczego nie pyta).
  useEffect(() => {
    if (!record || preview === null || previewVersion === recordVersion) return;
    let alive = true;
    fetchViaWritePreview(record.id)
      .then((p) => {
        if (!alive) return;
        setPreview(p);
        setPreviewVersion(recordVersion);
      })
      .catch(() => {
        if (!alive) return;
        // Nie wiemy, jak wygląda plik po zmianie — lepiej nie pokazywać
        // żadnego podglądu niż nieaktualny.
        setPreview(null);
        setPreviewVersion(null);
      });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recordVersion]);

  if (!record) return null;

  // Po każdej akcji odświeżamy podgląd, więc jego `stan` jest świeższy niż
  // rekord z react-admina — ten dojdzie dopiero po `refresh()`.
  const writeState =
    (!stale ? preview?.state : undefined) ?? String(record.viaWriteStatus ?? DRAFT);

  const run = async (
    action: () => Promise<unknown>,
    message: string,
    refreshPreview = true,
    failureTitle = 'Nie udało się',
  ) => {
    setBusy(true);
    setActionError(null);
    try {
      // Akcja może oddać własny komunikat — ponowienie dopisuje numer próby
      // i to, co serwer zauważył w skrzynce.
      const ownMessage = await action();
      notify(typeof ownMessage === 'string' ? ownMessage : message, {
        type: 'success',
      });
      if (refreshPreview) {
        setPreview(await fetchViaWritePreview(record.id));
        setPreviewVersion(recordVersion);
      }
      refresh();
    } catch (e) {
      // Odmowa z serwera jest pisana dla operatora („Zlecenie jest już
      // w VIA — zmiana wymaga usunięcia i wprowadzenia ponownie") i ma do
      // niego dotrzeć w całości.
      notify(toErrorMessage(e), { type: 'error' });
      setActionError({ title: failureTitle, message: toErrorMessage(e) });
      // Nieudane dostarczenie zostawia ślad na serwerze (zapis „błąd”
      // z komunikatem, 15.10.2026) — pokazujemy go zamiast starego stanu.
      if (refreshPreview) {
        try {
          setPreview(await fetchViaWritePreview(record.id));
          setPreviewVersion(recordVersion);
        } catch {
          // Podgląd zostaje, jaki był — odmowę operator już widzi.
        }
        refresh();
      }
    } finally {
      setBusy(false);
      // Ekran wokół panelu (okno kontroli) pokazuje stan zapisu osobno —
      // po każdej akcji, także nieudanej, czyta go od nowa.
      if (refreshPreview) onChanged?.();
    }
  };

  const hasErrors = (preview?.errors.length ?? 0) > 0;
  // Podobne zlecenia w banku (16.10.2026, za przełącznikiem): dopóki
  // operator nie potwierdzi „to nie duplikat”, serwer odmówi pliku — tu
  // tylko nie dajemy kliknąć w przycisk, który skończy się odmową.
  const duplicates = duplicatesOn && preview && !stale ? (preview.duplicates ?? null) : null;
  const duplicateBlock = duplicates?.writeRefusal ?? null;
  // Zlecenie z wózkiem: jedna akcja, dwa pliki (16.10.2026).
  const twoFiles = (preview?.files?.length ?? 0) > 1;

  return (
    <SectionCard title="Zapis do VIA (W10)">
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: 'block', mb: 1 }}
      >
        Plik XML powstaje wyłącznie po jawnym zatwierdzeniu. Po przyjęciu
        zlecenia w VIA zmiana wymaga usunięcia i wprowadzenia ponownie —
        nadpisanie zrobiłoby drugie zlecenie.
      </Typography>

      {writeState === CONFIRMED_IN_TMS && <TmsConfirmation record={record} tmsNames={tmsExport} />}

      {actionError && (
        <Alert
          severity="error"
          sx={{ mb: 1 }}
          data-testid="via-write-action-error"
          onClose={() => setActionError(null)}
        >
          <AlertTitle>{actionError.title}</AlertTitle>
          {actionError.message}
        </Alert>
      )}

      {preview && !stale && <DeliveryStatus preview={preview} tmsNames={tmsExport} />}

      {preview && !stale && <WriteFiles preview={preview} tmsNames={tmsExport} />}

      {/* Ponawianie automatyczne i ślad prób (21.10.2026, `viaDeliveryAutoRetry`). */}
      {preview && !stale && preview.deliveryRetry && <DeliveryRetryInfo retry={preview.deliveryRetry} />}

      {/* Ślad prób dostarczenia (poprawka obowiązkowa 21.10.2026, bez przełącznika). */}
      {preview && !stale && <AttemptTrail attempts={preview.attempts ?? []} />}

      {tmsExport && (
        <TmsExportDialog
          open={exportOpen}
          preview={stale ? null : preview}
          duplicateBlock={duplicateBlock}
          busy={busy}
          onClose={() => setExportOpen(false)}
          onConfirm={() =>
            void run(
              async () => {
                const checksums = (preview?.files ?? []).map((f) => f.checksum ?? '');
                try {
                  await exportToTms(record.id, checksums);
                } finally {
                  // Okno zamyka się także po błędzie — wynik stoi w panelu
                  // (25.10.2026); otwarte zostawało z odmową „plik już wyszedł”.
                  setExportOpen(false);
                }
                return twoFiles ? 'Zapisano: pliki wysłane do TMS' : 'Zapisano: plik wysłany do TMS';
              },
              'Zapisano',
              true,
              'Eksport do TMS nie powiódł się',
            )
          }
        />
      )}

      {duplicates && (
        <ViaWriteDuplicates
          check={duplicates}
          reason={notDuplicateReason}
          onReasonChange={setNotDuplicateReason}
          busy={busy}
          onConfirm={(ids) =>
            void run(async () => {
              await confirmNotDuplicate(record.id, ids, notDuplicateReason);
              setNotDuplicateReason('');
            }, 'Zapisano: to nie duplikat')
          }
        />
      )}

      <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', mb: 1 }} useFlexGap>
        {/*
          Eksport jedną akcją (21.10.2026): „Eksportuj do TMS” otwiera podgląd
          obu plików, a „Potwierdź zapis” w oknie zatwierdza i wytwarza pliki.
          Zatwierdzić osobno się nie da — po pliku zostaje „Ponów”.
        */}
        {tmsExport && EXPORTABLE.includes(writeState) && (
          <Button
            size="small"
            variant="contained"
            startIcon={<SendIcon />}
            disabled={busy}
            data-testid="adm-zlecenie-eksportuj-tms"
            onClick={() => {
              // Okno od razu, podgląd zaraz po nim — bez komunikatu: samo
              // otwarcie niczego nie zmienia na serwerze.
              setExportOpen(true);
              setBusy(true);
              fetchViaWritePreview(record.id)
                .then((p) => {
                  setPreview(p);
                  setPreviewVersion(recordVersion);
                })
                .catch((e) => {
                  notify(toErrorMessage(e), { type: 'error' });
                  setExportOpen(false);
                })
                .finally(() => setBusy(false));
            }}
          >
            Eksportuj do TMS
          </Button>
        )}

        {(!tmsExport || !EXPORTABLE.includes(writeState)) && (
          <Button
            size="small"
            variant="outlined"
            startIcon={<VisibilityIcon />}
            disabled={busy}
            onClick={() =>
              void run(
                async () => {
                  setPreview(await fetchViaWritePreview(record.id));
                  setPreviewVersion(recordVersion);
                },
                'Podgląd odświeżony',
                false,
              )
            }
          >
            Sprawdź plik
          </Button>
        )}

        {!tmsExport && (
          <Button
            size="small"
            variant="contained"
            color="success"
            startIcon={<CheckCircleIcon />}
            // Zatwierdzić można tylko szkic, i tylko gdy podgląd nie pokazuje
            // braków. Zatwierdzenie pliku, który VIA odrzuci, przesuwałoby
            // problem na moment, w którym nikt się o nim nie dowie.
            disabled={busy || stale || writeState !== DRAFT || preview === null || hasErrors}
            onClick={() =>
              void run(() => approveViaWrite(record.id), 'Zlecenie zatwierdzone')
            }
            // Kontrakt `data-testid` paczki (S-22.10-02): zatwierdzenie zapisu (bez eksportu jedną akcją).
            data-testid="adm-zlecenie-zatwierdz"
          >
            Zatwierdź
          </Button>
        )}

        {!tmsExport && (
          <Button
            size="small"
            variant="contained"
            startIcon={<SendIcon />}
            // O tym, czy wolno zapisać, rozstrzyga `gotowyDoZapisu` z serwera.
            // Powtórzenie tej reguły w przeglądarce dałoby dwie wersje prawdy
            // — a rozjechałyby się przy pierwszej zmianie w `zapis-via.service`.
            disabled={busy || stale || !preview?.canWrite || duplicateBlock !== null}
            onClick={() =>
              void run(
                () => generateViaFile(record.id),
                twoFiles ? 'Pliki wytworzone' : 'Plik wytworzony',
                true,
                'Plik nie został dostarczony',
              )
            }
            // Kontrakt `data-testid` paczki (S-22.10-02): „zapisz w TMS” — u nas plik dla VIA.
            data-testid="adm-zlecenie-zapisz-tms"
          >
            {twoFiles ? 'Wytwórz pliki' : 'Wytwórz plik'}
          </Button>
        )}

        {/*
          Ponowienie ma sens dopiero, gdy plik już wyszedł: do tego momentu
          jest „Wytwórz plik". Serwer i tak odmówi po przyjęciu w VIA —
          tu tylko nie pokazujemy przycisku, którego nie da się użyć.
          Eksport jedną akcją nie ma „Wytwórz plik”, a eksportuje się tylko
          szkic — po nieudanej próbie (np. plik w katalogu aplikacji, który nie
          trafił do TMS: serwer mówi wtedy `canWrite: true`) jedyną drogą jest
          „Ponów wysłanie do TMS” (integracja 25.10.2026, e2e s30).
        */}
        {writeState !== DRAFT &&
          preview !== null &&
          !stale &&
          (!preview.canWrite || (tmsExport && !EXPORTABLE.includes(writeState))) && (
          <Button
            size="small"
            variant="outlined"
            startIcon={<ReplayIcon />}
            disabled={busy || duplicateBlock !== null}
            data-testid={tmsExport ? 'adm-bledy-ponow' : undefined}
            onClick={() =>
              void run(
                async () => {
                  const w = await retryViaWrite(record.id);
                  return w.note
                    ? `Plik położony ponownie (próba ${w.attempt}). ${w.note}`
                    : `Plik położony ponownie (próba ${w.attempt})`;
                },
                'Plik położony ponownie',
                true,
                tmsExport ? 'Ponowne wysłanie do TMS nie powiodło się' : 'Ponowienie nie powiodło się',
              )
            }
          >
            {tmsExport ? 'Ponów wysłanie do TMS' : 'Połóż plik ponownie'}
          </Button>
        )}

        {/*
          Zlecenie przyjęte w VIA da się odblokować tylko w jeden sposób:
          człowiek usuwa je u klienta i oświadcza, że to zrobił. Niczego
          w VIA nie usuwamy — katalog komunikatów nie zna anulowania.
        */}
        {IN_VIA.includes(writeState) && (
          <Button
            size="small"
            variant="outlined"
            color="warning"
            startIcon={<UndoIcon />}
            disabled={busy || withdrawReason.trim().length < 10}
            onClick={() =>
              void run(async () => {
                const w = await withdrawFromVia(record.id, withdrawReason);
                setWithdrawReason('');
                return w.description;
              }, 'Zlecenie wróciło do szkicu')
            }
          >
            Usunąłem w VIA — wróć do szkicu
          </Button>
        )}

        {busy && <CircularProgress size={20} sx={{ alignSelf: 'center' }} />}
      </Stack>

      {IN_VIA.includes(writeState) && (
        <TextField
          label="Powód wycofania (wymagany, trafia do dziennika)"
          fullWidth
          size="small"
          value={withdrawReason}
          onChange={(e: ChangeEvent<HTMLInputElement>) =>
            setWithdrawReason(e.target.value)
          }
          helperText="Napisz, co się stało i kiedy usunięto zlecenie w VIA — po miesiącu nikt tego nie odtworzy z pamięci."
          sx={{ mb: 1 }}
        />
      )}

      {stale && (
        <Alert severity="info" sx={{ mt: 1 }}>
          Dane zlecenia zmieniły się — odświeżam podgląd pliku.
        </Alert>
      )}

      {preview === null && (
        <Typography variant="body2" color="text.secondary">
          {tmsExport && EXPORTABLE.includes(writeState)
            ? 'Kliknij „Eksportuj do TMS”, żeby zobaczyć pliki, które pójdą do VIA, i czego jeszcze brakuje.'
            : 'Kliknij „Sprawdź plik", żeby zobaczyć, co pójdzie do VIA i czego jeszcze brakuje.'}
        </Typography>
      )}

      {preview && hasErrors && (
        <Alert severity="error" sx={{ mt: 1 }}>
          <AlertTitle>
            Importer odrzuci ten plik — brakuje{' '}
            {plural(preview.errors.length, 'pola', 'pól', 'pól')}
          </AlertTitle>
          <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
            {preview.errors.map((b) => (
              <li key={b}>
                <Typography variant="body2">{b}</Typography>
              </li>
            ))}
          </Box>
        </Alert>
      )}

      {preview && !stale && !hasErrors && (
        <Alert severity="success" sx={{ mt: 1 }}>
          Plik ma komplet pól wymaganych przez importer.
        </Alert>
      )}

      {preview && preview.ostrzezenia.length > 0 && (
        <Alert severity="warning" sx={{ mt: 1 }}>
          <AlertTitle>Do sprawdzenia ({preview.ostrzezenia.length})</AlertTitle>
          <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
            {preview.ostrzezenia.map((o) => (
              <li key={o}>
                <Typography variant="body2">{o}</Typography>
              </li>
            ))}
          </Box>
        </Alert>
      )}

      {preview && preview.blockReason && (
        <Alert severity="info" sx={{ mt: 1 }}>
          {preview.blockReason}
        </Alert>
      )}

      {preview && (
        <Box sx={{ mt: 1 }}>
          <Chip
            size="small"
            variant="outlined"
            label={xmlVisible ? 'Ukryj treść pliku' : 'Pokaż treść pliku'}
            onClick={() => setXmlVisible(!xmlVisible)}
          />
          {xmlVisible &&
            // Przy dwóch plikach — oba, każdy z nazwą; przy jednym jak dotąd.
            ((preview.files?.length ?? 0) > 1
              ? (preview.files ?? []).map((f) => ({ key: f.kind, title: `${f.label} — ${f.fileName}`, xml: f.xml }))
              : [{ key: 'MAIN', title: null as string | null, xml: preview.xml }]
            ).map((f) => (
              <Box key={f.key} sx={{ mt: 1 }}>
                {f.title && (
                  <Typography variant="caption" sx={{ fontWeight: 600 }}>
                    {f.title}
                  </Typography>
                )}
                <Box
                  component="pre"
                  sx={{
                    mt: 0.5,
                    p: 1,
                    maxHeight: 320,
                    overflow: 'auto',
                    fontSize: 12,
                    bgcolor: 'action.hover',
                    borderRadius: 1,
                  }}
                >
                  {f.xml}
                </Box>
              </Box>
            ))}
        </Box>
      )}
    </SectionCard>
  );
};

/** Stan panelu należy do rekordu — przejście na inny rekord zaczyna od zera (`recordScoped`). */
export const ViaWritePanel = recordScoped(ViaWriteBody);
